import cx from 'classnames'
import { useRef } from 'react'
import { useSliderState } from '@react-stately/slider'
import { useSlider, useSliderThumb } from '@react-aria/slider'
import Styles from './Slider.module.scss'

import { SliderProps, ThumbProps } from './Types'
import { useFocusRing } from '@react-aria/focus'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { mergeProps } from '@react-aria/utils'
import { useNumberFormatter } from '@react-aria/i18n'

const Thumb = (props: ThumbProps) => {
  const { state, trackRef, index, name, size = 'md' } = props
  const inputRef = useRef(null)
  const { thumbProps, inputProps, isDragging } = useSliderThumb(
    {
      index,
      trackRef,
      inputRef,
      name,
    },
    state,
  )

  const { focusProps, isFocusVisible } = useFocusRing()
  return (
    <div
      {...thumbProps}
      className={cx(Styles.thumb, Styles[size], { [Styles.focus]: isFocusVisible, [Styles.dragging]: isDragging })}
    >
      <VisuallyHidden>
        <input ref={inputRef} {...mergeProps(inputProps, focusProps)} />
      </VisuallyHidden>
    </div>
  )
}

const Slider = ({ pinnedValue, size = 'md', ...props }: SliderProps) => {
  const trackRef = useRef(null)
  const numberFormatter = useNumberFormatter()
  const state = useSliderState({ ...props, numberFormatter })
  const { groupProps, trackProps } = useSlider(props, state, trackRef)

  return (
    <div {...groupProps} className={cx(Styles.slider, { [Styles.disabled]: state.isDisabled })}>
      <div {...trackProps} ref={trackRef} className={cx(Styles.track, Styles[size])}>
        {pinnedValue !== undefined ? (
          <>
            <div
              aria-label={`Pinned value: ${pinnedValue}`}
              className={Styles.pin}
              style={{ marginLeft: `${state.getValuePercent(pinnedValue) * 100}%` }}
            />
            <div
              className={cx(Styles.pinDeltaTrack, Styles[size])}
              style={{
                left: `calc(${
                  (state.getThumbValue(0) < pinnedValue
                    ? state.getThumbPercent(0)
                    : state.getValuePercent(pinnedValue)) * 100
                }% + 3px)`,
                width: `${Math.abs((state.getValuePercent(pinnedValue) - state.getThumbPercent(0)) * 100)}%`,
              }}
            />
          </>
        ) : null}
        {props.filled && pinnedValue === undefined && (
          <div
            className={cx(Styles.filledTrack, Styles[size])}
            style={{
              width: `${state.getThumbPercent(0) * 100}%`,
            }}
          />
        )}
        <Thumb index={0} state={state} trackRef={trackRef} name={props.name} size={size} />
      </div>
    </div>
  )
}

export { Slider }
